"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useGamesBySlugs } from "@/lib/api/hooks";
import { WAGERING_POLL_MS, rollupProgressView, useBonuses, useRollups } from "@/lib/api/incentives";
import {
  freeSpinGameCodes,
  isActiveReward,
  rewardAction,
  rewardView,
  trancheProgressView,
  useAccountRewards,
  useClaimRakeback,
  useClaimRewards,
  useRakeback,
} from "@/lib/api/rewards";
import { useBankrolls } from "@/lib/api/transfers";
import { formatAmount, withCurrency } from "@/lib/format/amount";
import {
  hasRewardActivity,
  markRewardsSeen,
  rewardTransitions,
  seenRewardIds,
  snapshotRewards,
  type RewardSnapshot,
  type RewardTransition,
} from "@/lib/rewards-activity";
import { RewardCard } from "@/components/account/RewardCard";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/primitives";
import { useToasts, type ToastInput } from "@/components/ui/Toast";

/* The gift button's panel, on the kit's `dropdown-rewards` frame: a header,
   the same reward items the rewards page draws — glyph, value, chips, Claim,
   and the wagering strip when one is mid-requirement — and a full-width
   Rewards History link at the foot. The rakeback pot gets the first row when
   it holds anything, the way the kit leads with its claimables.

   Data is the same query the rewards page uses, polled while the top bar is
   mounted (the platform pushes no reward events; the whitelabel polls the
   same way), which is also what feeds the two hooks below: the unseen dot and
   the status-change toasts. */

const POLL_MS = 60_000;

export function RewardsPanel({ onClose }: { onClose: () => void }) {
  const bankrolls = useBankrolls(true);
  const bankroll = bankrolls.data?.at(0);
  /* This panel draws wagering bars, so while it is open both halves of a bar
     are polled at the rewards cadence rather than the background one — the
     bar has to move as the player bets. The panel is mounted only while open;
     the indicator and toast hooks below stay on POLL_MS, so the app-wide
     request rate is unchanged when it is shut. */
  const rewards = useAccountRewards(true, { refetchInterval: WAGERING_POLL_MS });
  const claimRewards = useClaimRewards();
  const rakeback = useRakeback(bankroll?.id, true);
  const claimRakeback = useClaimRakeback(bankroll?.id);
  const rollups = useRollups(true, { refetchInterval: WAGERING_POLL_MS });

  /* The same joins the rewards page makes, so the dropdown draws the identical
     item: the bonus behind the reward for its name and art, and the free
     spin's game resolved by code — never by `siteGameId`, which the catalogue
     does not know. See YourRewards. */
  const bonuses = useBonuses();
  const bonusById = new Map((bonuses.data ?? []).map((bonus) => [bonus.id, bonus]));
  const spinCodes = [...new Set(rewards.rewards.flatMap(freeSpinGameCodes))];
  const spinGames = useGamesBySlugs(spinCodes);
  const gameBySlug = new Map(spinGames.games.map((game) => [game.slug, game]));

  /* One claim at a time; only the pressed card says Claiming…. */
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const claimOne = (id: string) => {
    if (claimingId !== null || claimRewards.isPending) return;
    setClaimingId(id);
    claimRewards.mutate([id], { onSettled: () => setClaimingId(null) });
  };

  const now = Date.now();
  const list = rewards.rewards.filter((reward) => isActiveReward(reward, now)).slice(0, 8);
  const scaleFor = new Map((bankrolls.data ?? []).map((entry) => [entry.id, entry.token]));
  const scale = bankroll?.token;
  const rakebackClaimable = rakeback.data?.claimable ?? "0";
  const rollupByBonusId = new Map((rollups.data ?? []).map((rollup) => [rollup.bonusId, rollup]));

  const gameFor = (code: string | undefined) => (code ? gameBySlug.get(code) : undefined);

  return (
    <div className="flex max-h-[70vh] flex-col">
      <div className="flex items-center gap-[8px] border-b border-border px-[16px] py-[12px]">
        <Icon name="gift" size={18} className="text-brand-bold" />
        <p className="flex-1 text-[14px] font-bold leading-[1.6]">Rewards</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close rewards"
          className="grid h-[24px] w-[24px] place-items-center text-fg transition-colors hover:text-fg-muted"
        >
          <Icon name="close" size={16} />
        </button>
      </div>

      <div className="scrollbar-none flex-1 overflow-y-auto p-[12px]">
        {rakebackClaimable !== "0" && scale && (
          <div className="mb-[8px] flex items-center justify-between gap-[8px] rounded-lg bg-elevation-2 p-[12px]">
            <div className="min-w-0">
              <p className="text-[14px] font-bold leading-[1.6]">Rakeback</p>
              <p className="text-[12px] font-black leading-[1.5] tabular-nums text-success-fg">
                {withCurrency(formatAmount(rakebackClaimable, scale), scale.symbol)}
              </p>
            </div>
            <Button
              size="sm"
              disabled={claimRakeback.isPending}
              onClick={() => claimRakeback.mutate(rakebackClaimable)}
            >
              {claimRakeback.isPending ? "Claiming…" : "Claim"}
            </Button>
          </div>
        )}

        {rewards.isPending && (
          <div className="flex flex-col gap-[8px]">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[76px] animate-pulse rounded-lg bg-elevation-2" />
            ))}
          </div>
        )}

        {rewards.isError && (
          <p className="px-[4px] py-[24px] text-center text-[12px] leading-[1.6] text-fg-subtle">
            Your rewards didn’t load. Try again in a moment.
          </p>
        )}

        {rewards.isSuccess && list.length === 0 && rakebackClaimable === "0" && (
          <div className="flex flex-col items-center gap-[8px] py-[32px] text-center">
            <Icon name="gift" size={22} className="text-fg-subtle" />
            <p className="text-[12px] leading-[1.6] text-fg-subtle">No rewards available</p>
          </div>
        )}

        {list.length > 0 && (
          <ul className="flex flex-col gap-[8px]">
            {list.map((reward) => (
              <RewardCard
                key={reward.id}
                reward={reward}
                now={now}
                scale={scaleFor.get(reward.siteBankrollId)}
                progress={(() => {
                  const rollup = reward.bonusId ? rollupByBonusId.get(reward.bonusId) : undefined;
                  const wp = rollup?.wageringProgress;
                  const tokenScale = scaleFor.get(reward.siteBankrollId);

                  if (rollup && wp) {
                    return {
                      percent: rollupProgressView(rollup).percent,
                      note: tokenScale
                        ? `${withCurrency(formatAmount(wp.wageredAmount, tokenScale), tokenScale.symbol)}/${withCurrency(formatAmount(wp.targetAmount, tokenScale), tokenScale.symbol)}`
                        : `${wp.wageredAmount}/${wp.targetAmount}`,
                    };
                  }

                  /* The tranche measures itself when no rollup exists — this
                     dropdown is where the full-bar-at-zero was reported. */
                  return trancheProgressView(reward.tranche, tokenScale);
                })()}
                bonus={reward.bonusId ? bonusById.get(reward.bonusId) : undefined}
                gameName={gameFor(freeSpinGameCodes(reward).at(0))?.title}
                action={rewardAction(reward, now, {
                  claimingId: claimingId === reward.id ? reward.id : null,
                  playHref: (() => {
                    const game = gameFor(freeSpinGameCodes(reward).at(0));
                    return game?.slug ? `/game/${game.slug}` : undefined;
                  })(),
                })}
                onClaim={() => claimOne(reward.id)}
                onOpen={onClose}
              />
            ))}
          </ul>
        )}
      </div>

      <Link
        href="/rewards"
        onClick={onClose}
        className="block border-t border-border px-[16px] py-[12px] text-center text-[13px] font-bold leading-[1.6] transition-colors hover:bg-elevation-3"
      >
        Rewards History
      </Link>
    </div>
  );
}

/* The dot on the gift button: claimable, or unseen since the panel was last
   opened. Computed in an effect because the seen-set lives in localStorage,
   which the server render cannot read — a hydration guess would flicker. */
export function useRewardsIndicator(enabled: boolean) {
  const rewards = useAccountRewards(enabled, { refetchInterval: POLL_MS });
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!enabled) return setActive(false);
    const live = rewards.rewards.filter((reward) => isActiveReward(reward, Date.now()));
    setActive(hasRewardActivity(live, seenRewardIds()));
  }, [enabled, rewards.rewards]);

  const markSeen = () => {
    markRewardsSeen(rewards.rewards.map((reward) => reward.id));
    setActive(hasRewardActivity(rewards.rewards, seenRewardIds()));
  };

  return { active, markSeen };
}

const TRANSITION_TOAST: Record<
  RewardTransition["kind"],
  Pick<ToastInput, "tone" | "body">
> = {
  claimable: { tone: "success", body: "Ready to claim" },
  "wagering-completed": { tone: "success", body: "Wagering Completed" },
  "wagering-exhausted": { tone: "info", body: "Wagering Requirement Exceeded" },
  expired: { tone: "info", body: "Offer Expired" },
};

/* Watches the polled list and announces genuine transitions, on the kit's
   snackbar frames — green for a completion, info blue for a change of state.
   The first sighting seeds the baseline silently; a page load is not news. */
export function useRewardStatusToasts(enabled: boolean) {
  const rewards = useAccountRewards(enabled, { refetchInterval: POLL_MS });
  const bankrolls = useBankrolls(enabled);
  const { push } = useToasts();
  const baseline = useRef<Map<string, RewardSnapshot> | null>(null);

  const scaleFor = new Map((bankrolls.data ?? []).map((entry) => [entry.id, entry.token]));

  useEffect(() => {
    if (!enabled || !rewards.isSuccess) return;

    if (baseline.current === null) {
      baseline.current = snapshotRewards(rewards.rewards);
      return;
    }

    const transitions = rewardTransitions(baseline.current, rewards.rewards);
    baseline.current = snapshotRewards(rewards.rewards);

    for (const transition of transitions) {
      const view = rewardView(transition.reward, Date.now(), scaleFor.get(transition.reward.siteBankrollId));

      push({
        ...TRANSITION_TOAST[transition.kind],
        title: view.title,
        href: "/rewards",
        hrefLabel: "Rewards",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rewards.isSuccess, rewards.dataUpdatedAt]);
}
